import { useEffect } from "react";

const DEFAULT_TITLE = "Yuva Ayurvedic Clinic Prayagraj | Ayurvedic Treatment Since 2008";
const DEFAULT_DESC = "Yuva Ayurvedic Clinic Private Limited — Prayagraj's most trusted Ayurvedic clinic for sexual health. 18+ years experience. 50,000+ satisfied patients. 100% confidential. Natural treatment, no side effects. Book now.";
const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_yuva-modern/artifacts/d1s5jglr_Untitled_design__1_-removebg-preview.png";
// Indian local + Hinglish + international men's sexual wellness keyword mix.
// Note: Panchakarma and Yoga/Naturopathy terms deliberately excluded per brand scope.
const DEFAULT_KEYWORDS = [
  // Indian + Hinglish
  "Ayurvedic clinic Prayagraj",
  "Ayurvedic doctor Prayagraj",
  "Gupt rog specialist Prayagraj",
  "Prayagraj mein gupt rog doctor",
  "Prayagraj mein mardana kamzori ka Ayurvedic ilaaj",
  "Mardana kamzori ka ilaaj Prayagraj",
  "Mardana shakti badhane ka natural tarika",
  "Sexual weakness ka Ayurvedic upay Prayagraj",
  "Shighrapatan ka ilaaj Prayagraj",
  "Napunsakta ka Ayurvedic ilaaj",
  "Best sex clinic Prayagraj",
  "Men's health clinic Prayagraj",
  "Best Ayurvedic clinic Uttar Pradesh",
  // International + NRI
  "Ayurvedic treatment for erectile dysfunction India",
  "Men's sexual health clinic India",
  "Natural cure for sexual weakness India",
  "Ayurvedic sexologist Prayagraj India",
  "Male sexual wellness Ayurveda India",
  "Best men's clinic Uttar Pradesh India",
  "Confidential sexual health treatment India",
  "NRI men's health treatment Prayagraj",
  "Online Ayurvedic consultation India",
].join(", ");

function upsertMeta(selector, attr, value) {
  if (typeof document === "undefined") return;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    const [name, val] = selector.replace("meta[", "").replace("]", "").split("=");
    tag.setAttribute(name, val.replace(/"/g, ""));
    document.head.appendChild(tag);
  }
  tag.setAttribute(attr, value);
}

function upsertLink(rel, href, attrs = {}) {
  if (typeof document === "undefined") return;
  const selector = attrs.hreflang
    ? `link[rel="${rel}"][hreflang="${attrs.hreflang}"]`
    : `link[rel="${rel}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    if (attrs.hreflang) tag.setAttribute("hreflang", attrs.hreflang);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

/**
 * Per-route SEO — sets title, description, keywords, Open Graph, Twitter card,
 * canonical and hreflang tags (en-IN, en, x-default).
 */
export default function useSeo({
  title,
  description,
  image,
  keywords,
  path,
  absoluteTitle = false,
} = {}) {
  useEffect(() => {
    const finalTitle = title
      ? (absoluteTitle ? title : `${title} · Yuva Ayurvedic Clinic Private Limited`)
      : DEFAULT_TITLE;
    const finalDesc = description || DEFAULT_DESC;
    const finalImage = image || DEFAULT_IMAGE;
    const merged = Array.from(new Set([
      ...(keywords ? keywords.split(/\s*,\s*/) : []),
      ...DEFAULT_KEYWORDS.split(/\s*,\s*/),
    ].map(k => k.trim()).filter(Boolean))).join(", ");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const pathname = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const url = origin + pathname;

    document.title = finalTitle;

    upsertMeta('meta[name="description"]', "content", finalDesc);
    upsertMeta('meta[name="keywords"]', "content", merged);
    upsertMeta('meta[name="robots"]', "content", "index, follow, max-image-preview:large");

    upsertMeta('meta[property="og:type"]', "content", "website");
    upsertMeta('meta[property="og:title"]', "content", finalTitle);
    upsertMeta('meta[property="og:description"]', "content", finalDesc);
    upsertMeta('meta[property="og:image"]', "content", finalImage);
    upsertMeta('meta[property="og:url"]', "content", url);
    upsertMeta('meta[property="og:site_name"]', "content", "Yuva Ayurvedic Clinic Private Limited");
    upsertMeta('meta[property="og:locale"]', "content", "en_IN");
    upsertMeta('meta[property="og:locale:alternate"]', "content", "en_US");

    upsertMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "content", finalTitle);
    upsertMeta('meta[name="twitter:description"]', "content", finalDesc);
    upsertMeta('meta[name="twitter:image"]', "content", finalImage);

    upsertLink("canonical", url);
    upsertLink("alternate", url, { hreflang: "en-IN" });
    upsertLink("alternate", url, { hreflang: "en" });
    upsertLink("alternate", url, { hreflang: "x-default" });
  }, [title, description, image, keywords, path, absoluteTitle]);
}
